import { Component, EventEmitter, Output, computed, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { CounselorNav } from '../../counselor-nav/counselor-nav';
import { Student } from '../../../../../services/student.service';
import { SchoolUser } from '../../../../../services/role.service';
import { AssignDialog } from './assign-dialog/assign-dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AssignService } from '../../../../../services/assign-faculty.service';

import { HasPermissionDirective } from '../../../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../../../constants/permissions';
import { UserAvatar } from '../../../../common/user-avatar/user-avatar';

export interface FacultyConnection {
  connectionId: number;
  faculty: SchoolUser;
  locked: boolean;
}

export interface StudentAssignment {
  id: string;
  name: string;
  grade: string;
  connections: FacultyConnection[];
}

interface CsvAssignResult {
  staffEmail: string;
  studentEmail: string;
  connectionId?: number;
  reason?: string;
}

type StatusFilter = 'all' | 'missing';

@Component({
  selector: 'app-assign-students',
  imports: [
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    CounselorNav,
    MatToolbarModule,
    HasPermissionDirective,
    UserAvatar,
  ],
  templateUrl: './assign-students.html',
  styleUrl: './assign-students.css',
})
export class AssignStudents {
  assignService = inject(AssignService);

  private dialog: MatDialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);

  readonly displayedColumns = ['select', 'name', 'grade', 'faculty', 'status'];
  readonly detailColumns = ['expandedDetail'];

  permissions = USER_PERMISSIONS;
  facultyMembers = signal<SchoolUser[]>([]);
  schoolStudents = signal<Student[]>([]);
  studentUsers = signal<SchoolUser[]>([]);

  students = signal<StudentAssignment[]>([]);

  searchTerm = signal('');
  statusFilter = signal<StatusFilter>('all');
  selectedIds = signal<Set<string>>(new Set());
  bulkFacultyId = signal<string>('');

  expandedIds = signal<Set<string>>(new Set());

  private pendingFacultyId = signal<Record<string, string>>({});

  filteredStudents = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const onlyMissing = this.statusFilter() === 'missing';

    return this.students().filter((student) => {
      const matchesTerm = !term || student.name.toLowerCase().includes(term);
      const matchesStatus = !onlyMissing || student.connections.length === 0;
      return matchesTerm && matchesStatus;
    });
  });

  missingCount = computed(() => this.students().filter((s) => s.connections.length === 0).length);

  assignedCount = computed(() => this.students().length - this.missingCount());

  selectedCount = computed(() => this.selectedIds().size);

  allVisibleSelected = computed(() => {
    const visible = this.filteredStudents();
    if (visible.length === 0) return false;
    const selected = this.selectedIds();
    return visible.every((s) => selected.has(s.id));
  });

  someVisibleSelected = computed(() => {
    const visible = this.filteredStudents();
    const selected = this.selectedIds();
    const anySelected = visible.some((s) => selected.has(s.id));
    return anySelected && !this.allVisibleSelected();
  });

  ngOnInit(): void {
    this.loadData();
  }

  // Load in all students and school faculty
  loadData(): void {
    forkJoin({
      students: this.assignService.getAllSchoolStudents(),
      users: this.assignService.getSchoolUsers(),
    }).subscribe(async ({ students, users }) => {
      const faculty = users.filter((u) => u.user_type === 'faculty');
      const studentUsers = users.filter((u) => u.user_type === 'student');

      this.facultyMembers.set(faculty);
      this.schoolStudents.set(students);
      this.studentUsers.set(studentUsers);

      const assignments = await this.buildAssignments(students, studentUsers);

      this.students.set(assignments);
    });
  }
  private async buildAssignments(
    students: Student[],
    users: SchoolUser[],
  ): Promise<StudentAssignment[]> {
    const usersById = new Map(users.map((u) => [u.id, u]));

    const assignments = await Promise.all(
      students.map(async (student): Promise<StudentAssignment | null> => {
        const user = usersById.get(student.user_id);

        if (!user) return null;

        const allConnections = await firstValueFrom(
          this.assignService.getConnections(student.user_id),
        );

        const connections: FacultyConnection[] = allConnections
          .map((connection) => {
            const faculty =
              connection.requester_id === student.user_id
                ? connection.addressee
                : connection.requester;

            if (faculty?.user_type !== 'faculty') return null;

            return {
              connectionId: connection.id,
              faculty,
              locked: connection.locked ?? false,
            };
          })
          .filter((connection): connection is FacultyConnection => connection !== null);

        return {
          id: String(student.id),
          name: this.displayName(user),
          grade: this.gradeLabel(student.grade_level),
          connections,
        };
      }),
    );

    return assignments.filter((assignment): assignment is StudentAssignment => assignment !== null);
  }

  displayName(user: SchoolUser): string {
    const first = user.preferredName?.trim() || user.first_name;
    return `${first} ${user.last_name}`;
  }

  facultyName(facultyId: string): string {
    const faculty = this.facultyMembers().find((f) => String(f.id) === facultyId);
    return faculty ? this.displayName(faculty) : 'Unknown';
  }

  initials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  private gradeLabel(gradeLevel: number): string {
    const v = gradeLevel % 100;
    const suffix = v >= 11 && v <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][gradeLevel % 10] ?? 'th');
    return `${gradeLevel}${suffix}`;
  }

  setSearchTerm(term: string): void {
    this.searchTerm.set(term);
  }

  setStatusFilter(filter: StatusFilter): void {
    this.statusFilter.set(filter);
  }

  isExpanded(studentId: string): boolean {
    return this.expandedIds().has(studentId);
  }

  toggleExpand(studentId: string): void {
    this.expandedIds.update((current) => {
      const next = new Set(current);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }

  visibleConnections(student: StudentAssignment, max = 3): FacultyConnection[] {
    return student.connections.slice(0, max);
  }

  overflowCount(student: StudentAssignment, max = 3): number {
    return Math.max(0, student.connections.length - max);
  }

  availableFacultyOptions(student: StudentAssignment): SchoolUser[] {
    const connectedIds = new Set(student.connections.map((c) => String(c.faculty.id)));

    return this.facultyMembers().filter((faculty) => !connectedIds.has(String(faculty.id)));
  }

  pendingFacultyIdFor(studentId: string): string {
    return this.pendingFacultyId()[studentId] ?? '';
  }

  setPendingFaculty(studentId: string, facultyId: string): void {
    this.pendingFacultyId.update((current) => ({ ...current, [studentId]: facultyId }));
  }

  addConnection(studentId: string, facultyId: string): void {
    if (!facultyId) return;

    const faculty = this.facultyMembers().find((f) => String(f.id) === facultyId);

    if (!faculty) return;

    const student = this.schoolStudents().find((s) => String(s.id) === studentId);

    if (!student) return;

    const assignment = this.students().find((s) => s.id === studentId);

    if (assignment?.connections.some((c) => String(c.faculty.id) === facultyId)) {
      return;
    }

    this.assignService.createConnection(student.user_id, faculty.id).subscribe({
      next: (connection) => {
        this.students.update((list) =>
          list.map((s) => {
            if (s.id !== studentId) return s;

            return {
              ...s,
              connections: [
                ...s.connections,
                {
                  connectionId: connection.id,
                  faculty,
                  locked: false,
                },
              ],
            };
          }),
        );

        this.setPendingFaculty(studentId, '');
      },

      error: (err) => {
        console.error('Could not create connection', err);
      },
    });
  }

  removeConnection(studentId: string, facultyId: number): void {
    const assignment = this.students().find((s) => s.id === studentId);

    if (!assignment) return;

    const target = assignment.connections.find((c) => c.faculty.id === facultyId);

    if (!target || target.locked) return;

    const student = this.schoolStudents().find((s) => String(s.id) === studentId);

    if (!student) return;

    this.assignService.removeConnection(target.connectionId, student.user_id).subscribe({
      next: () => {
        this.students.update((list) =>
          list.map((s) => {
            if (s.id !== studentId) return s;

            return {
              ...s,
              connections: s.connections.filter((c) => c.connectionId !== target.connectionId),
            };
          }),
        );
      },

      error: (err) => {
        console.error('Could not remove connection', err);
      },
    });
  }

  toggleLock(studentId: string, facultyId: number): void {
    const assignment = this.students().find((s) => s.id === studentId);

    if (!assignment) return;

    const target = assignment.connections.find((c) => c.faculty.id === facultyId);

    if (!target) return;

    const student = this.schoolStudents().find((s) => String(s.id) === studentId);

    if (!student) return;

    this.assignService.lockConnection(target.connectionId, student.user_id).subscribe({
      next: (result) => {
        this.students.update((list) =>
          list.map((s) => {
            if (s.id !== studentId) return s;

            return {
              ...s,
              connections: s.connections.map((c) =>
                c.connectionId === target.connectionId ? { ...c, locked: result.locked } : c,
              ),
            };
          }),
        );
      },
      error: (err) => {
        console.error('Could not toggle connection lock', err);
      },
    });
  }

  isSelected(studentId: string): boolean {
    return this.selectedIds().has(studentId);
  }

  toggleSelected(studentId: string): void {
    this.selectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }

  toggleSelectAllVisible(): void {
    const visible = this.filteredStudents();
    const shouldSelectAll = !this.allVisibleSelected();

    this.selectedIds.update((current) => {
      const next = new Set(current);
      for (const student of visible) {
        if (shouldSelectAll) {
          next.add(student.id);
        } else {
          next.delete(student.id);
        }
      }
      return next;
    });
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  applyBulkAssign(): void {
    const facultyId = this.bulkFacultyId();

    if (!facultyId || this.selectedCount() === 0) return;

    const faculty = this.facultyMembers().find((f) => String(f.id) === facultyId);

    if (!faculty) return;

    const targetIds = this.selectedIds();

    const studentsToConnect = this.schoolStudents().filter((student) =>
      targetIds.has(String(student.id)),
    );

    studentsToConnect.forEach((student) => {
      const assignment = this.students().find((s) => s.id === String(student.id));

      if (!assignment) return;

      if (assignment.connections.some((c) => c.faculty.id === faculty.id)) {
        return;
      }

      this.assignService.createConnection(student.user_id, faculty.id).subscribe({
        next: (connection) => {
          this.students.update((list) =>
            list.map((s) => {
              if (s.id !== String(student.id)) return s;

              return {
                ...s,
                connections: [
                  ...s.connections,
                  {
                    connectionId: connection.id,
                    faculty,
                    locked: false,
                  },
                ],
              };
            }),
          );
        },

        error: (err) => {
          console.error(
            `Could not connect student ${student.user_id} to faculty ${faculty.id}`,
            err,
          );
        },
      });
    });

    this.clearSelection();
    this.bulkFacultyId.set('');
  }

  openCsvUpload(): void {
    const dialogRef = this.dialog.open(AssignDialog, {
      width: '780px',
      maxWidth: '95vw',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.data) {
        this.showCsvSummary(result.data);
        this.loadData();
      }
    });
  }

  private showCsvSummary(summary: {
    created: CsvAssignResult[];
    skipped: CsvAssignResult[];
    errors: CsvAssignResult[];
  }): void {
    const createdCount = summary.created?.length ?? 0;
    const skippedCount = summary.skipped?.length ?? 0;
    const errorCount = summary.errors?.length ?? 0;

    const parts = [`${createdCount} assigned`];
    if (skippedCount) parts.push(`${skippedCount} skipped`);
    if (errorCount) parts.push(`${errorCount} error${errorCount === 1 ? '' : 's'}`);

    const message = parts.join(', ');
    const panelClass = errorCount > 0 ? 'cd-snackbar-warn' : 'cd-snackbar-success';

    this._snackBar.open(message, 'Dismiss', {
      duration: errorCount > 0 ? 8000 : 5000,
      panelClass,
    });
  }
}
